import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers } from 'formik'
import Alert from '@material-ui/lab/Alert'
import Button from '@material-ui/core/Button'
import { extractError, getEditedPayload, isFieldError } from 'utils/api'
import * as yup from 'yup'
import { ITempleResponse } from 'types/api/temple';
import { useIntl } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import { IApiState } from 'store/types';
import { apiStatus } from 'shared/constants/AppEnum';
import FormikTextField from 'components/Fields/FormikTextField';
import MenuItem from '@material-ui/core/MenuItem';
import { Box, Checkbox, Divider, FormControlLabel, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

export type ComplianceFormType = {
    tds: number,
    commissionPercent: '' | number,
    commissionMin: '' | number,
    commissionMax: '' | number,
    enableOffering: boolean,
    enableDonation: boolean,
    enableFinancial: boolean,
    enableInventory: boolean,
    enableDakshina: boolean,
    enableStore: boolean,
    enableMarriage: boolean,
    enableHall: boolean,
    enableRoom: boolean,
    enableElephant: boolean,
    slug: string,
    defaultGateway?: 'RP' | 'EB' | 'CL'
}

const getInitialValues = (temple?: ITempleResponse): ComplianceFormType => {
    return {
        tds: temple?.tds ?? 0,
        commissionPercent: temple && (temple.commissionPercent || temple.commissionPercent === 0) ? temple.commissionPercent : '',
        commissionMin: temple?.commissionMin ?? '',
        commissionMax: temple?.commissionMax ?? '',
        slug: temple?.slug ?? '',
        enableDonation: temple?.enableDonation ?? true,
        enableFinancial: temple?.enableFinancial ?? true,
        enableInventory: temple?.enableInventory ?? true,
        enableOffering: temple?.enableOffering ?? true,
        enableDakshina: temple?.enableDakshina ?? true,
        enableStore: temple?.enableStore ?? true,
        enableMarriage: temple?.enableMarriage ?? true,
        enableHall: temple?.enableHall ?? true,
        enableRoom: temple?.enableRoom ?? true,
        enableElephant: temple?.enableElephant ?? true,
        defaultGateway: temple?.defaultGateway ?? 'RP'
    }
}

const formToTemple = (formValues: ComplianceFormType): Partial<ITempleResponse> => {
    return {
        ...formValues,
        tds: formValues.tds,
        commissionPercent: formValues.commissionPercent || formValues.commissionPercent === 0 ? formValues.commissionPercent : null,
        commissionMin: formValues.commissionMin ? formValues.commissionMin : null,
        commissionMax: formValues.commissionMax ? formValues.commissionMax : null,
        defaultGateway: formValues.defaultGateway

    }
}


interface ComplianceFormProps {
    temple?: ITempleResponse
    handleSubmit: (values: Partial<ITempleResponse>, actions: FormikHelpers<ComplianceFormType>) => void
    api: IApiState
}

const ComplianceForm: React.FC<ComplianceFormProps> = ({ temple, handleSubmit, api }) => {
    const { messages } = useIntl()
    const initialValues = useMemo(() => getInitialValues(temple), [temple])

    const validationSchema = yup.object({
        tds: yup.number()
            .required('TDS is required'),
        commissionPercent: yup.number().min(0).max(100),
        commissionMin: yup.number(),
        slug: yup.string(),

        commissionMax: yup.number().when('commissionMin', (commMin: any, schema: any) => {
            if (commMin !== '')
                return schema.min(commMin)
            return schema
        }),
        defaultGateway: yup.mixed().oneOf(['RP', 'EB', 'CL'])
    })

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
                handleSubmit(getEditedPayload(formToTemple(initialValues), formToTemple(values)), actions)
            }}
            enableReinitialize
        >
            {({ isValid, dirty, isSubmitting, setFieldValue, values }) => (
                <Form noValidate autoComplete='off'>
                    <Grid container spacing={2}>

                        <Grid item xs={12}>

                            <FormikTextField
                                select
                                name='tds'
                                variant='outlined'
                                required
                                fullWidth
                                placeholder={messages['compliance.tds']}
                                label={messages['compliance.tds']}
                                size="small"
                            >
                                {[0, 1, 5, 20].map((val, index) => (
                                    <MenuItem key={index} value={val}>
                                        {val}%
                                    </MenuItem>
                                ))}
                            </FormikTextField>
                            <FormikTextField
                                placeholder={'Slug'}
                                name='slug'
                                label={'Slug'}
                                variant='outlined'
                                fullWidth
                                size="small"
                            />
                            <Typography style={{ fontWeight: 600, marginTop: 16 }}>Commission</Typography>
                            <Divider />
                            <FormikTextField
                                placeholder={messages['commission.percent']}
                                name='commissionPercent'
                                label={messages['commission.percent']}
                                variant='outlined'
                                type='number'
                                fullWidth
                                size="small"
                            />

                            <FormikTextField
                                placeholder={messages['commission.min']}
                                name='commissionMin'
                                label={messages['commission.min']}
                                variant='outlined'
                                type='number'
                                fullWidth
                                size="small"
                            />
                            <FormikTextField
                                placeholder={messages['commission.max']}
                                name='commissionMax'
                                label={messages['commission.max']}
                                variant='outlined'
                                type='number'
                                fullWidth
                                size="small"
                            />
                            <Grid item xs={12}>
                                <Typography style={{ marginBottom: 6 }}>Default Gateway</Typography>
                                <ToggleButtonGroup size='small' value={values.defaultGateway}>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'RP')} value={'RP'}>Razorpay</ToggleButton>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'EB')} value={'EB'}>Easebuzz</ToggleButton>
                                    <ToggleButton onClick={() => setFieldValue('defaultGateway', 'CL')} value={'CL'}>Clover</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            <Typography style={{ fontWeight: 600, marginTop: 16 }}>Modules</Typography>
                            <Divider />
                            <Box style={{ display: 'flex' }}>
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableOffering', e.target.checked) }}
                                    checked={values.enableOffering}
                                    color='primary'
                                />} label="Enable Offering" />
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableDonation', e.target.checked) }}
                                    checked={values.enableDonation}
                                    color='primary'
                                />} label="Enable Donation" />
                            </Box>
                            <Box style={{ display: 'flex' }}>
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableInventory', e.target.checked) }}
                                    checked={values.enableInventory}
                                    color='primary'
                                />} label="Enable Inventory" />
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableFinancial', e.target.checked) }}
                                    checked={values.enableFinancial}
                                    color='primary'
                                />} label="Enable FInancial" />
                            </Box>
                            <Box style={{ display: 'flex' }}>
                                <FormControlLabel control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableDakshina', e.target.checked) }}
                                    checked={values.enableDakshina}
                                    color='primary'
                                />} label="Enable Dakshina" />
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableStore', e.target.checked) }}
                                    checked={values.enableStore}
                                    color='primary'
                                />} label="Enable Store" />
                            </Box>

                            <Box style={{ display: 'flex' }}>
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableMarriage', e.target.checked) }}
                                    checked={values.enableMarriage}
                                    color='primary'
                                />} label="Enable Marriage" />
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableHall', e.target.checked) }}
                                    checked={values.enableHall}
                                    color='primary'
                                />} label="Enable Hall" />
                            </Box>

                            <Box style={{ display: 'flex' }}>
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableRoom', e.target.checked) }}
                                    checked={values.enableRoom}
                                    color='primary'
                                />} label="Enable Room" />
                                <FormControlLabel style={{ flex: 1 }} control={<Checkbox
                                    onChange={(e) => { setFieldValue('enableElephant', e.target.checked) }}
                                    checked={values.enableElephant}
                                    color='primary'
                                />} label="Enable Elephant" />
                            </Box>

                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                disabled={isSubmitting || !isValid || !dirty}
                                variant="contained"
                                color="primary"
                                type="submit"
                            >
                                {messages['common.save']}
                            </Button>
                        </Grid>
                        {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                            <Grid item xs={12}>
                                <Alert severity="error">{extractError(api.error)}</Alert>
                            </Grid>
                        )}
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};

export default ComplianceForm;